import { useState, useEffect, useCallback } from 'react';
import { Button, Input, Toggle } from '@/components/ui';
import { SectionLoader } from '@/components/common/SectionLoader';
import { useCompanies } from '@/features/companies/hooks/useCompanies';
import { useCreateAnnouncement, useUpdateAnnouncement } from '../hooks/useAnnouncements';
import { cn } from '@/lib/cn';
import type { Announcement, CreateAnnouncementPayload } from '@/types/announcement.types';

interface AnnouncementFormProps {
  announcement?: Announcement | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export function AnnouncementForm({
  announcement,
  onSuccess,
  onCancel,
}: AnnouncementFormProps): React.ReactElement {
  const isEdit = !!announcement;

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [audienceType, setAudienceType] = useState<'ORGANIZATION' | 'COMPANY'>('ORGANIZATION');
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [sendEmail, setSendEmail] = useState(false);
  const [sendNotification, setSendNotification] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [isPinned, setIsPinned] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');

  const { data: companies = [], isLoading: companiesLoading } = useCompanies();

  const createMutation = useCreateAnnouncement();
  const updateMutation = useUpdateAnnouncement();

  useEffect(() => {
    if (announcement) {
      setTitle(announcement.title);
      setContent(announcement.content);
      setAudienceType(announcement.audienceType);
      setSelectedCompanyIds(
        announcement.targetCompanyIds?.map((c) => (typeof c === 'string' ? c : c._id)) ?? []
      );
      setSendEmail(announcement.sendEmail);
      setSendNotification(announcement.sendNotification);
      setShowBanner(announcement.showBanner);
      setIsPinned(announcement.isPinned);
      setExpiresAt(
        announcement.expiresAt ? new Date(announcement.expiresAt).toISOString().slice(0, 16) : ''
      );
    }
  }, [announcement]);

  const handleCompanyToggle = useCallback((companyId: string) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      const payload: CreateAnnouncementPayload = {
        title: title.trim(),
        content: content.trim(),
        audienceType,
        targetCompanyIds: audienceType === 'COMPANY' ? selectedCompanyIds : undefined,
        sendEmail,
        sendNotification,
        showBanner,
        isPinned,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      };

      if (isEdit && announcement) {
        updateMutation.mutate({ id: announcement._id, payload }, { onSuccess });
      } else {
        createMutation.mutate(payload, { onSuccess });
      }
    },
    [
      title,
      content,
      audienceType,
      selectedCompanyIds,
      sendEmail,
      sendNotification,
      showBanner,
      isPinned,
      expiresAt,
      isEdit,
      announcement,
      createMutation,
      updateMutation,
      onSuccess,
    ]
  );

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isValid =
    title.trim().length > 0 &&
    content.trim().length > 0 &&
    (audienceType !== 'COMPANY' || selectedCompanyIds.length > 0);

  return (
    <form onSubmit={handleSubmit} className="space-y-7">
      {/* ── Section: Content ───────────────────────── */}
      <fieldset className="space-y-4 border-b border-gray-200 pb-7 dark:border-gray-700">
        <legend className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Content
        </legend>

        <Input
          id="ann-title"
          label="Title"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter announcement title"
        />

        <div>
          <label
            htmlFor="ann-content"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Content <span className="text-danger">*</span>
          </label>
          <textarea
            id="ann-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your announcement content..."
            required
            rows={5}
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
        </div>
      </fieldset>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* ── Section: Audience ──────────────────────── */}
        <fieldset className="space-y-4 rounded-xl border border-gray-200 bg-gray-50/60 p-5 dark:border-gray-700 dark:bg-gray-800/40">
          <legend className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Audience
          </legend>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setAudienceType('ORGANIZATION')}
              className={cn(
                'rounded-lg border px-4 py-2 text-sm font-medium transition-colors',
                audienceType === 'ORGANIZATION'
                  ? 'border-primary bg-primary/10 text-primary dark:bg-primary/20'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
              )}
            >
              Entire Organization
            </button>
            <button
              type="button"
              onClick={() => setAudienceType('COMPANY')}
              className={cn(
                'rounded-lg border px-4 py-2 text-sm font-medium transition-colors',
                audienceType === 'COMPANY'
                  ? 'border-primary bg-primary/10 text-primary dark:bg-primary/20'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
              )}
            >
              Specific Companies
            </button>
          </div>

          {audienceType === 'COMPANY' && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Select Companies <span className="text-danger">*</span>
              </label>
              {companiesLoading ? (
                <SectionLoader variant="list" rows={3} label="Loading companies" />
              ) : companies.length === 0 ? (
                <p className="text-sm text-gray-500">No companies found in your organization.</p>
              ) : (
                <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-600 dark:bg-gray-800">
                  {companies.map((company) => (
                    <label
                      key={company._id}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                        selectedCompanyIds.includes(company._id)
                          ? 'bg-primary/5 text-primary dark:bg-primary/10'
                          : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCompanyIds.includes(company._id)}
                        onChange={() => handleCompanyToggle(company._id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-600"
                      />
                      {company.name}
                    </label>
                  ))}
                </div>
              )}
              {selectedCompanyIds.length > 0 && (
                <p className="mt-1.5 text-xs text-gray-500">
                  {selectedCompanyIds.length} company(ies) selected
                </p>
              )}
            </div>
          )}
        </fieldset>

        {/* ── Section: Delivery ──────────────────────── */}
        <fieldset className="space-y-4 rounded-xl border border-gray-200 bg-gray-50/60 p-5 dark:border-gray-700 dark:bg-gray-800/40">
          <legend className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Delivery Channels
          </legend>

          <div className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
            <div className="px-4 py-3">
              <Toggle
                id="toggle-banner"
                checked={showBanner}
                onChange={setShowBanner}
                label="Show as Banner"
                align="end"
              />
            </div>
            <div className="px-4 py-3">
              <Toggle
                id="toggle-notification"
                checked={sendNotification}
                onChange={setSendNotification}
                label="Send In-App Notification + Desktop Push"
                align="end"
              />
            </div>
            <div className="px-4 py-3">
              <Toggle
                id="toggle-email"
                checked={sendEmail}
                onChange={setSendEmail}
                label="Send Email"
                align="end"
              />
            </div>
          </div>
        </fieldset>
      </div>

      {/* ── Section: Options ───────────────────────── */}
      <fieldset className="grid gap-4 rounded-xl border border-gray-200 bg-gray-50/60 p-5 dark:border-gray-700 dark:bg-gray-800/40 sm:grid-cols-2 sm:items-end">
        <legend className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Options
        </legend>

        <Input
          id="ann-expires"
          label="Expires At"
          type="datetime-local"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          hint="Leave empty if the announcement has no expiry."
        />
        <div className="flex min-h-10 items-center sm:pb-5">
          <Toggle
            id="toggle-pinned"
            checked={isPinned}
            onChange={setIsPinned}
            label="Pin to top of banner"
          />
        </div>
      </fieldset>

      {/* ── Actions ────────────────────────────────── */}
      <div className="flex flex-col gap-4 border-t border-gray-200 pt-5 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-gray-500 dark:text-gray-500">
          Saves as <strong>Draft</strong>. Publish from the list when ready.
        </p>
        <div className="flex items-center gap-3">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!isValid} loading={isPending}>
            {isEdit ? (
              'Update Draft'
            ) : (
              'Save as Draft'
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}

AnnouncementForm.displayName = 'AnnouncementForm';
